(function() {
	'use strict';

	/**
	 * Service for managing the User's db. (auth, _users database updates like metadata/settings)
	 */

	angular
		.module("pwc.pouch.user")
		.service("UserService", UserService);

	UserService.$inject = ['$window', 'pouchDB', 'lodash', '$log', '$q', 'userPouch', 'ENV', '$localStorage'];

	function UserService($window, pouchDB, lodash, $log, $q, userPouch, ENV, $localStorage){

		/* jshint validthis: true */
		var self = this;

		self.db = pouchDB(ENV.COUCH_URL + '/_users', {
			ajax: {
				cache: true // prevent cached request on IE
			},
			skip_setup: true,
			skipSetup: true
		});

		// These are the simple settings we want to store locally and not persist to CouchDB or maintain in a PouchDB.
		self.$localStorage = $localStorage.$default({
			theme: {
				primary: "blue",
				accent: "pink",
				forms: {
					boldInputs: true,
					labelOpacity: 65
				},
				dataGrid: {
					fontSize: 13,
					bold: false
				}
			},
			order: {
				column: 0,
				reverse: false
			},
			headers: [],
			columns: 8
		});

		self.updateUser = updateUser;
		self.updateSetting = updateSetting;
		self.getSetting = getSetting;
		self.addCustomFieldSetting = addCustomFieldSetting;
		self.getCustomFields = getCustomFields;
		self.getLocale = getLocale;
		self.setLocale = setLocale;
		self.removeCustomFieldSetting = removeCustomFieldSetting;
		self.setupLocale = setupLocale;

        /** initialize */
        initialize();

        /** begin methods */

        /**
         * Constructor / Initialization
         */
        function initialize(){
            setupLocale();
        }

		/**
		 * Updates a user's document in /_users and synchronizes with local user pouch.
		 * @param userDoc Document object
		 * @returns {*}
		 */
		function updateUser(userDoc){
			return self.db.put(userDoc)
				.then(function(response){
					// gotta get it back to get the full doc
					return self.db.get(response.id);
				})
				.catch(function(err){
					$log.debug('Issue updating user from UserService', err);
				});
		}

		function getUser(id){
			var userId = 'org.couchdb.user:' + id;
			$log.debug('Getting user from pouch...', userId);
			return self.db.get(userId);
		}

		/**
		 * This will save a setting to the user's metadata on couch/pouch.
		 * Save settings that should be available across ALL user devices.
		 * @param name
		 * @param val
		 */
		function updateSetting(name, val){
			$log.debug('Updating setting for', name, val);
			return userPouch.getActiveUser()
				.then(function(response){
					$log.debug('getActiveUser', response);

					var userSettings = lodash.get(response, 'metadata.settings');

					if (! userSettings){
						lodash.set(response, 'metadata.settings', {});
					}

					return getUser(response['name']);
				})
				.then(function(user){
                    $log.debug('Fresh user', user);
                    lodash.set(user, 'metadata.settings.' + name, val);

                    return updateUser(user);
                })
				.catch(function(err){
					$log.error('Issue getting active user while updating setting', err);
				});
		}

		/**
		 * Use this for appending a value to a setting that is an array, like custom fields.
		 * @param name
		 * @param val
		 * @returns {*}
		 */
		function appendSetting(name, val){
			$log.debug('Appending setting for', name, val);
			return userPouch.getActiveUser()
				.then(function(response){
					$log.debug('getActiveUser', response);

					return getUser(response['name'])
						.then(function(user){
							$log.debug('Fresh user', user);

							var userSettings = lodash.get(user, 'metadata.settings');

							if (! userSettings){
								lodash.set(user, 'metadata.settings', {});
							}

							var key = 'metadata.settings.' + name;

							var currentValue = lodash.get(user, key);

							if (! lodash.includes(currentValue, val)){
								if (! currentValue){
									user.metadata.settings[name] = [];
								}
								user.metadata.settings[name].push(val);
							}

							return updateUser(user);
						})
						.catch(function(err){
							$log.debug('Issue appending setting for user', response);
						});
				})
				.catch(function(err){
					$log.error('Issue getting active user while appending settings', err);
				});
		}

		/**
		 * Get user's setting which is stored on the central CouchDB user metadata.
		 * @param name string
		 * @param defaultVal Default value
		 * @returns Promise
		 */
		function getSetting(name, defaultVal){

			$log.debug('UserService: Getting User Setting', name);

			return userPouch.getActiveUser()
				.then(function(response){
					$log.debug('UserService:getSetting:getActiveUser', response);

					return getUser(response['name'])
						.then(function(user){
							$log.debug('Fresh user', user);
							return lodash.get(user, 'metadata.settings.' + name, defaultVal);
						})
						.catch(function(err){
							$log.warn('Issue getting user', err);
							return defaultVal;
						});
				})
				.catch(function(err){
					$log.warn('Issue getting active user', err);
					return defaultVal;
				});
		}

		/**
		 * get local settings in user couch, default to english / $
		 * @returns Promise
		 */
		function getLocale(){
			return getSetting('locale', 'en');
		}

		/**
		 * update numeral local and save locale to couch
		 * @param locale String
		 */
		function setLocale(locale){
			_activateLocale(locale);
			// store locale to couch
			updateSetting('locale', locale);
		}

		/**
		 * activate locale:
		 * - set local on numeraljs
		 * - update ENV
		 */
		function _activateLocale(locale){
			$window.numeral.locale(locale);
			if (lodash.get(numeral.locales, locale)){
				$log.debug('Activating Locale:', numeral.locales[locale]);
                ENV.LOCALE_SYMBOL = numeral.locales[locale].currency.symbol;
			}
		}

		/**
		 * Get locale from couch and set on numeraljs
		 * @return Promise
		 */
		function setupLocale(){
			return getLocale().then(function(locale){
				$log.debug('User Setting: locale', locale);
                _activateLocale(locale);
			});
		}

		function getCustomFields(){
			return getSetting('customFields', null);
		}

		function addCustomFieldSetting(fieldName){
			return appendSetting('customFields', fieldName);
		}

		function removeCustomFieldSetting(fieldName){
			$log.debug('Removing custom field', fieldName);
			return getCustomFields().then(function(customFields){
				if (lodash.isArray(customFields) && customFields.length > 0){
					var newSettings = lodash.reject(customFields, function(item) {
						return item == fieldName;
					});

					return updateSetting('customFields', newSettings);
				}

				return false;
			});
		}
	}

})();
